import { useEffect, useRef } from 'react';
import type React from 'react';

import { useInView } from 'react-intersection-observer';

import { SECTION_ID_MAPPING } from '#src/app/pages/petitionDetails/shared/constants';
import { useScrollToSection } from '#src/app/pages/petitionDetails/shared/hooks';

import { useMediaHitsContext } from '../../shared/context';
import { MediaHitList } from '../MediaHitList';
import { useMediaHitScrollControl } from '../MediaHitList/hooks/useMediaHitScrollControl';

import { MediaHitsHeader } from './components/MediaHitsHeader';
import { useMediaHits } from './hooks/useMediaHits';

export const MediaHitsWrapper: React.FC = () => {
	const {
		data: { showAdd, showSection, showList, hasMediaHits, trackingData },
	} = useMediaHits();
	const {
		actions: { loadEmbeds },
	} = useMediaHitsContext();

	const {
		data: { refs },
	} = useScrollToSection();

	const mediaHitListRef = useRef<HTMLDivElement>(null);
	const {
		data: { canScrollLeft, canScrollRight, hasOverflow },
		actions: { updateScrollState, scrollByOneMediaHit },
	} = useMediaHitScrollControl(mediaHitListRef);

	// Defer embed loading until the section nears the viewport. The trigger lives on an always-rendered sentinel
	// (rather than the section itself) because the section stays hidden until at least one embed resolves valid —
	// gating the trigger on the section would deadlock that case.
	const { ref: loadTriggerRef, inView } = useInView({ rootMargin: '600px', triggerOnce: true });
	useEffect(() => {
		if (inView) loadEmbeds();
	}, [inView, loadEmbeds]);

	// Update scroll state when content visibility changes (with delay for rendering)
	useEffect(() => {
		if (showList) {
			// Delay to allow MediaHitList to render its content
			const timer = setTimeout(updateScrollState, 100);
			return () => clearTimeout(timer);
		}

		return undefined;
	}, [showList, updateScrollState]);

	if (!showSection && !hasMediaHits) {
		return null;
	}

	// The scroll anchor (id + ref) stays mounted whenever there are media hits, even while the visible section is
	// still hidden pending embed resolution. Otherwise `scrollToSection(MEDIA_MENTIONS)` (fired by the content
	// summary / momentum bar links) would no-op, and the lazy load — which depends on this region scrolling into
	// view — would never trigger.
	return (
		<div id={SECTION_ID_MAPPING.MEDIA_MENTIONS} ref={refs[SECTION_ID_MAPPING.MEDIA_MENTIONS]}>
			{hasMediaHits && <span ref={loadTriggerRef} aria-hidden="true" className="block h-0 w-0" />}
			{showSection && (
				// eslint-disable-next-line @change-ds/ds-no-arbitrary-spacing-classname
				<div data-testid="media-hits-wrapper" className="mt-[52px] overflow-x-hidden">
					<MediaHitsHeader
						showAdd={showAdd}
						trackingData={trackingData}
						onScrollLeft={() => scrollByOneMediaHit('left')}
						onScrollRight={() => scrollByOneMediaHit('right')}
						canScrollLeft={canScrollLeft}
						canScrollRight={canScrollRight}
						showScrollButtons={hasOverflow}
					/>

					{showList && <MediaHitList ref={mediaHitListRef} />}
				</div>
			)}
		</div>
	);
};
